/**
 * The visas a library document or a skill's case filter can name, grouped the
 * way an immigration lawyer thinks about them. Unlike the case pickers this is
 * not gated by which workflows are switched on: a firm keeps PERM templates
 * whether or not it runs PERM cases here. Internal workflow values ("EB-2 NIW
 * v2", "Custom Workflow") never show; the v2 workflow reads as "EB-2 NIW".
 */
import { VISAVALUE } from "../../types/visa.type";

export type VisaGroup = { id: string; title: string; visas: string[] };

export const VISA_GROUPS: VisaGroup[] = [
  {
    id: "employment-immigrant",
    title: "Employment-based immigrant",
    visas: [
      VISAVALUE.EB1A,
      VISAVALUE.EB1B,
      VISAVALUE.EB1C,
      VISAVALUE.EB2NIW,
      VISAVALUE.PERM,
      VISAVALUE.I140_APPROVED_LC,
      VISAVALUE.SCHEDULE_A_G2,
      VISAVALUE.EB5,
      VISAVALUE.I829
    ]
  },
  {
    id: "nonimmigrant",
    title: "Nonimmigrant",
    visas: [
      VISAVALUE.H1B,
      VISAVALUE.H2A,
      VISAVALUE.H2B,
      VISAVALUE.H3,
      VISAVALUE.L1A,
      VISAVALUE.L1B,
      VISAVALUE.O1A,
      VISAVALUE.O1B,
      VISAVALUE.O2,
      VISAVALUE.P1A,
      VISAVALUE.P1B,
      VISAVALUE.P3,
      VISAVALUE.E1,
      VISAVALUE.E2,
      VISAVALUE.E3,
      VISAVALUE.TN,
      VISAVALUE.R1,
      VISAVALUE.B1,
      VISAVALUE.STEMOPT
    ]
  },
  {
    id: "family",
    title: "Family-based",
    visas: [VISAVALUE.I130, VISAVALUE.K1, VISAVALUE.I751, VISAVALUE.AOS]
  },
  {
    id: "humanitarian",
    title: "Humanitarian",
    visas: [
      VISAVALUE.I589,
      VISAVALUE.U,
      VISAVALUE.T,
      VISAVALUE.VAWA,
      VISAVALUE.SIJS,
      VISAVALUE.TPS,
      VISAVALUE.PAROLE_IN_PLACE,
      VISAVALUE.MILITARY_PAROLE_IN_PLACE
    ]
  },
  {
    id: "removal",
    title: "Removal defense",
    visas: [
      VISAVALUE.EOIR_42A,
      VISAVALUE.EOIR_42B,
      VISAVALUE.VAWA_CANCELLATION,
      VISAVALUE.BOND,
      VISAVALUE.I246,
      VISAVALUE.MOTION_TO_REOPEN_RECONSIDER,
      VISAVALUE.HABEAS,
      VISAVALUE.MANDAMUS
    ]
  },
  {
    id: "naturalization",
    title: "Naturalization",
    visas: [VISAVALUE.N400, VISAVALUE.N600]
  },
  {
    id: "waivers",
    title: "Waivers",
    visas: [
      VISAVALUE.I601,
      VISAVALUE.I601A,
      VISAVALUE.I212,
      VISAVALUE.I612,
      VISAVALUE.I602
    ]
  }
];

const ALIASES: Record<string, string> = {
  [VISAVALUE.EB2NIW_V2]: VISAVALUE.EB2NIW
};

const KNOWN = new Set(VISA_GROUPS.flatMap((g) => g.visas));

/** The catalog's spelling of a visa value, or null for one it does not list. */
export const canonicalVisa = (
  value: string | null | undefined
): string | null => {
  const text = (value ?? "").trim();
  if (!text) return null;
  const aliased = ALIASES[text] ?? text;
  if (KNOWN.has(aliased)) return aliased;
  const lower = aliased.toLowerCase();
  return [...KNOWN].find((v) => v.toLowerCase() === lower) ?? null;
};

/** Long names for the visas whose value alone is cryptic. */
const DESCRIPTIONS: Record<string, string> = {
  [VISAVALUE.EOIR_42A]: "Cancellation of removal (LPR)",
  [VISAVALUE.EOIR_42B]: "Cancellation of removal (non-LPR)",
  [VISAVALUE.AOS]: "Adjustment of status",
  [VISAVALUE.U]: "U visa",
  [VISAVALUE.T]: "T visa",
  [VISAVALUE.I589]: "I-589",
  [VISAVALUE.SIJS]: "Special Immigrant Juvenile",
  [VISAVALUE.TPS]: "Temporary Protected Status"
};

export const visaDescription = (value: string): string | undefined =>
  DESCRIPTIONS[value];

/** The group a visa sits in, for a compact "EB-1A · Employment-based" label. */
export const visaGroupOf = (value: string): VisaGroup | undefined =>
  VISA_GROUPS.find((g) => g.visas.includes(value));
